/**
 * Differ for the flat workflow (BPMN) collab documents.
 *
 * `diffWfDocument(before, after, makeOpId?)` emits an op list such that applying
 * it to a canonical `before` (via `applyWfOps`) yields `after` EXACTLY, including
 * all per-container ordering. Neither input is mutated.
 *
 * Emission order (load-bearing — apply executes ops sequentially, and UNDO
 * replays the inverses in strict reverse, so every intermediate state must be
 * reference-consistent in BOTH directions):
 *  1. Structural upserts + container moves, walking `after`'s containment DFS
 *     from the root process (""). Walking a container's children left to right
 *     guarantees every `afterSiblingId` anchor was either a survivor or has
 *     already been placed, so anchor-relative insertion reproduces the exact
 *     target order (same argument as sd-diff).
 *  2. Per-element field-group ops for surviving elements. These come BEFORE
 *     the removes on purpose: an endpoint/attach rewire away from a doomed
 *     element must happen while that element still exists, so that the
 *     reverse-played inverse (rewiring BACK to it) runs after the remove has
 *     been undone — otherwise the undo would skip with TARGET_GONE.
 *  3. Removes — one `wfElementRemove` per removed element, NO `cascadedIds`
 *     (the cascade stamp is the SERVER's; a client differ sees every removed
 *     element individually), emitted REFERENCERS-FIRST then DEEPEST-FIRST:
 *     an element that references another doomed element (a flow's
 *     sourceId/targetId, a boundary's attachedToId) is removed before its
 *     referent, and children before their containers. Reverse-played undo
 *     then restores containers before children AND endpoints before the
 *     connections that need them — without the reference tier, the undo
 *     restored a sequence flow before its source node and the canvas
 *     materializer silently dropped the connection (KOA-18191: task came
 *     back, the arrow to the end node did not). Survivors that escaped a
 *     removed container were already re-homed in step 1.
 *  4. Doc-level ops for the whitelisted roots.
 *
 * Structural notes:
 *  - New elements travel as `wfElementUpsert` (verbatim record clone, laneId
 *    included as local best knowledge — the server re-derives it) followed by a
 *    positioning `wfContainerSet`, EXCEPT when every later sibling in the
 *    container is also new: then the upsert's append already lands exactly.
 *  - Same-container reorders emit the minimal move set: the longest increasing
 *    subsequence (over before positions) of the surviving same-container
 *    siblings stays put; everything else gets a `wfContainerSet`.
 *  - `laneId` is SERVER-DERIVED and NEVER compared: a laneId-only difference
 *    emits no op at all, and the differ NEVER emits `wfLaneDerived` — that op
 *    kind exists solely as a server-origin broadcast.
 *  - top-level `name` diffs granularly as `wfPropSet`/`wfPropRemove` "/name"
 *    (routed to element.name by both appliers — semantics never carries name);
 *    it is NOT an upsert-fallback trigger.
 *
 * Field-group notes (surviving elements):
 *  - bounds/waypoints/labelBounds changes collapse into ONE `wfGeometrySet`
 *    carrying only the changed groups; endpoint changes into ONE
 *    `wfFlowEndpointsSet` (both endpoints always travel); `attachedToId` into
 *    `wfAttachSet`; `semantics` deep-diffs into granular `wfPropSet` /
 *    `wfPropRemove` (objects recurse, arrays are whole-value in v1 — no keyed
 *    selectors are EMITTED, though the appliers accept the full sd-path grammar).
 *  - Changes the vocabulary cannot express granularly — a non-grouped top-level
 *    field (bpmnType, eventDefinition, isDefaultFlow, …) or the REMOVAL of a
 *    grouped field (bounds/sourceId/attachedToId/… present before, absent
 *    after) — fall back to ONE whole-record `wfElementUpsert`, which is always
 *    exact and replaces every other field-group op for that element.
 */

import { longestIncreasingSubsequenceKeep } from "../sd-patch/sd-diff";
import { cloneJsonValue, CollabJsonValue, deepJsonEqual, ICollabJsonObject, isJsonObject } from "../sd-patch/sd-json";
import { buildPointer, CollabPathSegment } from "../sd-patch/sd-path";
import { containerChainDepth, containerIdOf, orderIndexOf } from "./wf-doc";
import { IWfCollabOp, makeWfOpId } from "./wf-op-types";
import { IWfBounds, IWfDocument, IWfElement, IWfPoint } from "./wf-types";

interface IWfElementPosition {
  containerId: string;
  index: number;
}

interface IWfDocView {
  /** containerId -> canonical ordered child ids (bucket entries first, unlisted members appended). */
  children: Map<string, string[]>;
  /** elementId -> its container and index within that container's canonical order. */
  position: Map<string, IWfElementPosition>;
}

/** Element-record keys owned by identity, the server, or a dedicated op group. */
const EXCLUDED_ELEMENT_DIFF_KEYS: ReadonlySet<string> = new Set<string>([
  "id",
  "laneId",
  "containerId",
  "name",
  "bounds",
  "waypoints",
  "labelBounds",
  "sourceId",
  "targetId",
  "attachedToId",
  "semantics"
]);

/** Grouped keys whose REMOVAL cannot be expressed by the group ops (upsert fallback). */
const GROUPED_ELEMENT_KEYS: readonly string[] = [
  "bounds",
  "waypoints",
  "labelBounds",
  "sourceId",
  "targetId",
  "attachedToId"
];

const WF_DOC_DIFF_ROOTS: readonly string[] = ["name", "parameters", "happyPathDefinition", "docExtras"];

function elementsOf(doc: IWfDocument): Record<string, IWfElement> {
  return isJsonObject(doc.elements as unknown) ? doc.elements : {};
}

/** Shallow copy of a semantics bag without its (never-legal) root "name" key. */
function withoutSemanticName(semantics: unknown): ICollabJsonObject {
  if (!isJsonObject(semantics)) {
    return {};
  }
  if (semantics["name"] === undefined) {
    return semantics;
  }
  const copy: ICollabJsonObject = { ...semantics };
  delete copy["name"];
  return copy;
}

function elementOrderOf(doc: IWfDocument): Record<string, string[]> {
  return isJsonObject(doc.elementOrder as unknown) ? doc.elementOrder : {};
}

/**
 * Canonical per-container child lists: `elementOrder` is authoritative for
 * ORDER, `containerId` for MEMBERSHIP. Bucket entries that no longer exist or
 * moved container are dropped; members a bucket forgot are appended in
 * element-record order. Total on slightly-inconsistent docs.
 */
function buildDocView(doc: IWfDocument): IWfDocView {
  const elements: Record<string, IWfElement> = elementsOf(doc);
  const order: Record<string, string[]> = elementOrderOf(doc);
  const children: Map<string, string[]> = new Map<string, string[]>();
  const seen: Set<string> = new Set<string>();
  for (const bucketKey of Object.keys(order)) {
    const bucket: string[] = order[bucketKey];
    if (!Array.isArray(bucket)) {
      continue;
    }
    const list: string[] = [];
    for (const id of bucket) {
      const element: IWfElement | undefined = elements[id];
      if (element === undefined || containerIdOf(element) !== bucketKey || seen.has(id)) {
        continue;
      }
      list.push(id);
      seen.add(id);
    }
    if (list.length > 0) {
      children.set(bucketKey, list);
    }
  }
  for (const id of Object.keys(elements)) {
    if (seen.has(id)) {
      continue;
    }
    const containerId: string = containerIdOf(elements[id]);
    const list: string[] | undefined = children.get(containerId);
    if (list === undefined) {
      children.set(containerId, [id]);
    } else {
      list.push(id);
    }
    seen.add(id);
  }
  const position: Map<string, IWfElementPosition> = new Map<string, IWfElementPosition>();
  for (const [containerId, list] of children) {
    for (let i = 0; i < list.length; i++) {
      position.set(list[i], { containerId, index: i });
    }
  }
  return { children, position };
}

export function diffWfDocument(
  before: IWfDocument,
  after: IWfDocument,
  makeOpId: () => string = makeWfOpId
): IWfCollabOp[] {
  const beforeElements: Record<string, IWfElement> = elementsOf(before);
  const afterElements: Record<string, IWfElement> = elementsOf(after);
  const beforeView: IWfDocView = buildDocView(before);
  const afterView: IWfDocView = buildDocView(after);
  const ops: IWfCollabOp[] = [];

  function emitPropSet(elementId: string, segments: readonly CollabPathSegment[], value: CollabJsonValue): void {
    ops.push({
      opId: makeOpId(),
      kind: "wfPropSet",
      elementId,
      path: buildPointer(segments),
      value: cloneJsonValue(value)
    });
  }

  function emitPropRemove(elementId: string, segments: readonly CollabPathSegment[]): void {
    ops.push({ opId: makeOpId(), kind: "wfPropRemove", elementId, path: buildPointer(segments) });
  }

  function emitDocPropSet(segments: readonly CollabPathSegment[], value: CollabJsonValue): void {
    ops.push({ opId: makeOpId(), kind: "wfDocPropSet", path: buildPointer(segments), value: cloneJsonValue(value) });
  }

  function emitDocPropRemove(segments: readonly CollabPathSegment[]): void {
    ops.push({ opId: makeOpId(), kind: "wfDocPropRemove", path: buildPointer(segments) });
  }

  function emitContainerSet(elementId: string, containerId: string, afterSiblingId: string | null): void {
    ops.push({ opId: makeOpId(), kind: "wfContainerSet", elementId, containerId, afterSiblingId });
  }

  function emitUpsert(element: IWfElement): void {
    ops.push({
      opId: makeOpId(),
      kind: "wfElementUpsert",
      elementId: element.id,
      element: cloneJsonValue(element as unknown as ICollabJsonObject) as unknown as IWfElement
    });
  }

  /** Ids of the same-container survivors that keep their relative order (LIS members). */
  function computeStableStayers(containerId: string, children: readonly string[]): Set<string> {
    const stayerIds: string[] = [];
    const stayerBeforeIndices: number[] = [];
    for (const id of children) {
      const beforePosition: IWfElementPosition | undefined = beforeView.position.get(id);
      if (beforePosition !== undefined && beforePosition.containerId === containerId) {
        stayerIds.push(id);
        stayerBeforeIndices.push(beforePosition.index);
      }
    }
    const keepPositions: Set<number> = longestIncreasingSubsequenceKeep(stayerBeforeIndices);
    const keep: Set<string> = new Set<string>();
    for (const keepPosition of keepPositions) {
      keep.add(stayerIds[keepPosition]);
    }
    return keep;
  }

  const walkedContainers: Set<string> = new Set<string>();

  /** Emits upserts and container moves for one container's after-order, left to right. */
  function walkContainer(containerId: string): void {
    walkedContainers.add(containerId);
    const children: string[] = afterView.children.get(containerId) ?? [];
    const stableStayers: Set<string> = computeStableStayers(containerId, children);
    // suffixHasSurvivor[i]: some sibling AFTER position i already existed in `before`.
    // When false, a new element's upsert-append lands exactly (every later sibling
    // is itself a new append), so the positioning wfContainerSet can be elided.
    const suffixHasSurvivor: boolean[] = new Array<boolean>(children.length).fill(false);
    for (let i = children.length - 2; i >= 0; i--) {
      suffixHasSurvivor[i] = suffixHasSurvivor[i + 1] || beforeView.position.has(children[i + 1]);
    }
    let previousSiblingId: string | null = null;
    for (let i = 0; i < children.length; i++) {
      const id: string = children[i];
      const element: IWfElement | undefined = afterElements[id];
      if (element === undefined) {
        continue;
      }
      const beforePosition: IWfElementPosition | undefined = beforeView.position.get(id);
      if (beforePosition === undefined) {
        emitUpsert(element);
        if (suffixHasSurvivor[i]) {
          emitContainerSet(id, containerId, previousSiblingId);
        }
      } else {
        const needsMove: boolean = beforePosition.containerId !== containerId || !stableStayers.has(id);
        if (needsMove) {
          emitContainerSet(id, containerId, previousSiblingId);
        }
      }
      if (!walkedContainers.has(id) && afterView.children.has(id)) {
        walkContainer(id);
      }
      previousSiblingId = id;
    }
  }

  /** Recursive value diff: objects recurse member-wise, everything else (arrays included) whole-value. */
  function diffValue(
    prevVal: CollabJsonValue,
    curVal: CollabJsonValue,
    segments: readonly CollabPathSegment[],
    emitSet: (segments: readonly CollabPathSegment[], value: CollabJsonValue) => void,
    emitRemove: (segments: readonly CollabPathSegment[]) => void
  ): void {
    if (deepJsonEqual(prevVal, curVal)) {
      return;
    }
    if (isJsonObject(prevVal) && isJsonObject(curVal)) {
      diffObjectMembers(prevVal, curVal, segments, emitSet, emitRemove);
      return;
    }
    emitSet(segments, curVal);
  }

  function diffObjectMembers(
    prevObj: ICollabJsonObject,
    curObj: ICollabJsonObject,
    baseSegments: readonly CollabPathSegment[],
    emitSet: (segments: readonly CollabPathSegment[], value: CollabJsonValue) => void,
    emitRemove: (segments: readonly CollabPathSegment[]) => void
  ): void {
    for (const key of Object.keys(curObj)) {
      const curVal: CollabJsonValue = curObj[key];
      if (curVal === undefined) {
        continue;
      }
      const segments: CollabPathSegment[] = [...baseSegments, { kind: "plain", key }];
      const prevVal: CollabJsonValue = prevObj[key];
      if (prevVal === undefined) {
        emitSet(segments, curVal);
      } else {
        diffValue(prevVal, curVal, segments, emitSet, emitRemove);
      }
    }
    for (const key of Object.keys(prevObj)) {
      if (prevObj[key] === undefined) {
        continue;
      }
      if (curObj[key] === undefined) {
        emitRemove([...baseSegments, { kind: "plain", key }]);
      }
    }
  }

  /** A non-grouped top-level field changed, or a grouped field was REMOVED → whole-record upsert. */
  function needsUpsertFallback(be: IWfElement, ae: IWfElement): boolean {
    const beObj: ICollabJsonObject = be as unknown as ICollabJsonObject;
    const aeObj: ICollabJsonObject = ae as unknown as ICollabJsonObject;
    for (const key of Object.keys(aeObj)) {
      if (EXCLUDED_ELEMENT_DIFF_KEYS.has(key) || aeObj[key] === undefined) {
        continue;
      }
      if (!deepJsonEqual(beObj[key], aeObj[key])) {
        return true;
      }
    }
    for (const key of Object.keys(beObj)) {
      if (EXCLUDED_ELEMENT_DIFF_KEYS.has(key) || beObj[key] === undefined) {
        continue;
      }
      if (aeObj[key] === undefined) {
        return true;
      }
    }
    for (const key of GROUPED_ELEMENT_KEYS) {
      if (beObj[key] !== undefined && aeObj[key] === undefined) {
        return true;
      }
    }
    // Endpoints must always travel as a PAIR: a change that leaves after with
    // only one endpoint present is inexpressible by wfFlowEndpointsSet.
    const endpointsChanged: boolean = be.sourceId !== ae.sourceId || be.targetId !== ae.targetId;
    if (endpointsChanged && (ae.sourceId === undefined) !== (ae.targetId === undefined)) {
      return true;
    }
    return false;
  }

  function diffSurvivorFieldGroups(be: IWfElement, ae: IWfElement): void {
    if (needsUpsertFallback(be, ae)) {
      emitUpsert(ae);
      return;
    }
    const geometryOp: IWfCollabOp = { opId: "", kind: "wfGeometrySet", elementId: ae.id };
    let geometryChanged: boolean = false;
    if (ae.bounds !== undefined && !deepJsonEqual(be.bounds, ae.bounds)) {
      geometryOp.bounds = cloneJsonValue(ae.bounds as unknown as ICollabJsonObject) as unknown as IWfBounds;
      geometryChanged = true;
    }
    if (ae.waypoints !== undefined && !deepJsonEqual(be.waypoints, ae.waypoints)) {
      geometryOp.waypoints = cloneJsonValue(ae.waypoints) as unknown as IWfPoint[];
      geometryChanged = true;
    }
    if (ae.labelBounds !== undefined && !deepJsonEqual(be.labelBounds, ae.labelBounds)) {
      if (ae.labelBounds === null) {
        geometryOp.labelBounds = null;
      } else {
        geometryOp.labelBounds = cloneJsonValue(ae.labelBounds as unknown as ICollabJsonObject) as unknown as IWfBounds;
      }
      geometryChanged = true;
    }
    if (geometryChanged) {
      geometryOp.opId = makeOpId();
      ops.push(geometryOp);
    }
    if (
      (be.sourceId !== ae.sourceId || be.targetId !== ae.targetId) &&
      ae.sourceId !== undefined &&
      ae.targetId !== undefined
    ) {
      ops.push({
        opId: makeOpId(),
        kind: "wfFlowEndpointsSet",
        elementId: ae.id,
        sourceId: ae.sourceId,
        targetId: ae.targetId
      });
    }
    if (be.attachedToId !== ae.attachedToId && ae.attachedToId !== undefined) {
      ops.push({ opId: makeOpId(), kind: "wfAttachSet", elementId: ae.id, hostId: ae.attachedToId });
    }
    // Top-level `name` diffs granularly as the "/name" prop pointer — the same
    // wire shape a semantics-key edit produces, but routed to element.name by
    // BOTH appliers (semantics never carries `name`).
    if (be.name !== ae.name) {
      if (ae.name !== undefined) {
        ops.push({ opId: makeOpId(), kind: "wfPropSet", elementId: ae.id, path: "/name", value: ae.name });
      } else {
        ops.push({ opId: makeOpId(), kind: "wfPropRemove", elementId: ae.id, path: "/name" });
      }
    }
    // Semantic-root "name" keys are defensively excluded: the "/name" pointer
    // belongs to the top-level field above, and a malformed doc must never make
    // the differ emit a pointer that would clobber it.
    const beSemantics: ICollabJsonObject = withoutSemanticName(be.semantics as unknown);
    const aeSemantics: ICollabJsonObject = withoutSemanticName(ae.semantics as unknown);
    diffObjectMembers(
      beSemantics,
      aeSemantics,
      [],
      (segments: readonly CollabPathSegment[], value: CollabJsonValue): void => emitPropSet(ae.id, segments, value),
      (segments: readonly CollabPathSegment[]): void => emitPropRemove(ae.id, segments)
    );
  }

  function diffDocFields(): void {
    const beforeObj: ICollabJsonObject = before as unknown as ICollabJsonObject;
    const afterObj: ICollabJsonObject = after as unknown as ICollabJsonObject;
    // participantBounds is a WHOLE-VALUE root: geometry must travel as one unit
    // (member-wise recursion would let racing clients converge to a mixed
    // rectangle — x from one client, width from another). Removal is never
    // emitted: the participant is not deletable through the editor, and both
    // appliers refuse wfDocPropRemove "/participantBounds" by contract.
    const beforePb: CollabJsonValue = before.participantBounds as unknown as CollabJsonValue;
    const afterPb: CollabJsonValue = after.participantBounds as unknown as CollabJsonValue;
    if (afterPb !== undefined && (beforePb === undefined || !deepJsonEqual(beforePb, afterPb))) {
      emitDocPropSet([{ kind: "plain", key: "participantBounds" }], afterPb);
    }
    for (const rootKey of WF_DOC_DIFF_ROOTS) {
      const prevVal: CollabJsonValue = beforeObj[rootKey];
      const curVal: CollabJsonValue = afterObj[rootKey];
      const segments: readonly CollabPathSegment[] = [{ kind: "plain", key: rootKey }];
      if (curVal === undefined) {
        if (prevVal !== undefined) {
          emitDocPropRemove(segments);
        }
        continue;
      }
      if (prevVal === undefined) {
        emitDocPropSet(segments, curVal);
        continue;
      }
      diffValue(prevVal, curVal, segments, emitDocPropSet, emitDocPropRemove);
    }
  }

  // 1. Structural upserts + container moves in after containment order.
  walkContainer("");
  for (const containerId of afterView.children.keys()) {
    // Defensive totality: containers unreachable from the root chain (malformed docs).
    if (!walkedContainers.has(containerId) && containerId !== "" && afterElements[containerId] !== undefined) {
      walkContainer(containerId);
    }
  }

  // 2. Field-group diffs for surviving elements (BEFORE removes — see module doc).
  for (const id of Object.keys(afterElements)) {
    const be: IWfElement | undefined = beforeElements[id];
    if (be !== undefined) {
      diffSurvivorFieldGroups(be, afterElements[id]);
    }
  }

  // 3. Removes — one op per removed element (cascade stamping is the server's
  //    job), referencers before their referents and children before containers
  //    so reverse-played undo restores referents/containers first (KOA-18191).
  const removedIds: string[] = Object.keys(beforeElements).filter(
    (id: string): boolean => afterElements[id] === undefined
  );
  const removedSet: Set<string> = new Set<string>(removedIds);
  const refDepthMemo: Map<string, number> = new Map<string, number>();
  const referencedRemovedIds = (id: string): string[] => {
    const el: IWfElement | undefined = beforeElements[id];
    if (el === undefined) {
      return [];
    }
    const refs: string[] = [];
    if (el.sourceId !== undefined && el.sourceId !== id && removedSet.has(el.sourceId)) {
      refs.push(el.sourceId);
    }
    if (el.targetId !== undefined && el.targetId !== id && removedSet.has(el.targetId)) {
      refs.push(el.targetId);
    }
    if (el.attachedToId !== undefined && el.attachedToId !== id && removedSet.has(el.attachedToId)) {
      refs.push(el.attachedToId);
    }
    return refs;
  };
  // refDepth 0 = references no other doomed element; a flow into a doomed node is 1;
  // a flow into a doomed boundary attached to a doomed node is 2. Higher = removed
  // earlier, so the reversed inverse restores it LAST — after everything it points at.
  const refDepthOf = (id: string, visiting: Set<string>): number => {
    const memoized: number | undefined = refDepthMemo.get(id);
    if (memoized !== undefined) {
      return memoized;
    }
    if (visiting.has(id)) {
      return 0; // malformed reference cycle — fall back to the containment/order keys
    }
    visiting.add(id);
    let depth: number = 0;
    for (const ref of referencedRemovedIds(id)) {
      const candidate: number = 1 + refDepthOf(ref, visiting);
      if (candidate > depth) {
        depth = candidate;
      }
    }
    visiting.delete(id);
    refDepthMemo.set(id, depth);
    return depth;
  };
  removedIds.sort((a: string, b: string): number => {
    const refDelta: number = refDepthOf(b, new Set<string>()) - refDepthOf(a, new Set<string>());
    if (refDelta !== 0) {
      return refDelta;
    }
    const depthDelta: number = containerChainDepth(before, b) - containerChainDepth(before, a);
    if (depthDelta !== 0) {
      return depthDelta;
    }
    const indexDelta: number = orderIndexOf(before, a) - orderIndexOf(before, b);
    if (indexDelta !== 0) {
      return indexDelta;
    }
    if (a === b) {
      return 0;
    }
    return a < b ? -1 : 1;
  });
  for (const id of removedIds) {
    ops.push({ opId: makeOpId(), kind: "wfElementRemove", elementId: id });
  }

  // 4. Doc-level fields.
  diffDocFields();

  return ops;
}
